<template>
  <v-flex xs12 sm6 md4 lg3 class="px-2 text-field">
    <component
      :is="componentType"
      v-model="modelValue"
      v-bind="$attrs"
      :items="items"
      :multiple="multiple"
      :clearable="true"
      :placeholder="placeholder"
      class="filter-field"
      dense
      hide-details
      :prepend-icon="icon"
      deletable-chips
      :type="type"
      :item-text="itemText"
      :item-value="itemValue"
      :return-object="false"
      :aria-label="placeholder"
      :aria-required="false"
      :aria-multiselectable="multiple"
    >
      <template v-slot:prepend>
        <div class="d-flex align-center icon-container">
          <v-icon small class="mr-1" aria-hidden="true">{{ icon }}</v-icon>
          <v-btn
            v-if="hasCondition"
            icon
            x-small
            @click.stop="$emit('toggle-condition')"
            :aria-label="getConditionToggleLabel()"
            class="condition-toggle"
          >
            <v-icon x-small>{{ value.condition === "in" ? "mdi-equal" : "mdi-not-equal" }}</v-icon>
          </v-btn>
          <v-btn disabled v-else icon x-small aria-hidden="true">
            <v-icon x-small>mdi-equal</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-if="multiple" v-slot:selection="{ item, index }">
        <v-chip small v-if="index === 0" :aria-label="getItemText(item)">
          <span class="caption">{{ getItemText(item) }}</span>
        </v-chip>
        <span
          v-if="index === 1"
          class="grey--text caption"
          :aria-label="`And ${modelValue.length - 1} more items selected`"
          >(+{{ modelValue.length - 1 }})</span
        >
      </template>
      <template v-slot:item="{ item }">
        <div v-if="item.type">
          <v-icon class="pr-3" color="red" v-if="item.type && item.type === 'expert'" x-small>mdi-account</v-icon>
          <v-icon class="pr-3" color="primary" v-else x-small>mdi-account</v-icon>
        </div>
        <div class="v-list-item__content caption">{{ getItemText(item) }}</div>
      </template>
      <template v-slot:no-data>
        <v-list>
          <div class="v-list-item__content">
            <div class="v-list-item__title">{{ $i18n.t("No results found") }}</div>
          </div>
        </v-list>
      </template>
    </component>
    <div class="sr-only" aria-live="polite">
      {{ getSelectedItemsAnnouncement() }}
    </div>
  </v-flex>
</template>

<script>
export default {
  name: "FilterField",
  props: {
    value: {
      required: true
    },
    type: {
      required: true
    },
    placeholder: {
      required: true
    },
    icon: {
      required: true
    },
    items: {
      default: () => []
    },
    multiple: {
      default: false
    }
  },
  computed: {
    componentType() {
      switch (this.type) {
        case "select":
          return "v-select";
        case "autocomplete":
          return "v-autocomplete";
        default:
          return "v-text-field";
      }
    },
    hasCondition() {
      return typeof this.value === "object" && "condition" in this.value;
    },
    modelValue: {
      get() {
        return this.hasCondition ? this.value.values : this.value;
      },
      set(newValue) {
        if (this.hasCondition) {
          this.$emit("input", { ...this.value, values: newValue });
        } else {
          this.$emit("input", newValue);
        }
      }
    },
    itemText() {
      return this.hasObjectItems ? "text" : undefined;
    },
    itemValue() {
      return this.hasObjectItems ? "value" : undefined;
    },
    hasObjectItems() {
      return (
        this.items.length > 0 &&
        typeof this.items[0] === "object" &&
        "text" in this.items[0] &&
        "value" in this.items[0]
      );
    },
    selectedItems() {
      if (!this.modelValue) return [];

      if (!Array.isArray(this.modelValue)) {
        return [this.modelValue];
      }

      return this.modelValue;
    }
  },
  methods: {
    getItemText(item) {
      if (this.hasObjectItems) {
        if (typeof item === "object" && "text" in item) {
          return item.text;
        }
        // If the item is just a value, find the corresponding object in items
        const foundItem = this.items.find(i => i.value === item);
        return foundItem ? foundItem.text : item;
      }
      return item;
    },
    getConditionToggleLabel() {
      const currentCondition = this.value.condition === "in" ? "include" : "exclude";
      const newCondition = this.value.condition === "in" ? "exclude" : "include";
      return `Toggle filter condition for ${this.placeholder} from ${currentCondition} to ${newCondition}`;
    },
    getSelectedItemsAnnouncement() {
      if (!this.selectedItems.length) return "";

      const itemTexts = this.selectedItems.map(item => this.getItemText(item));

      if (itemTexts.length === 1) {
        return `${this.placeholder} filter: ${itemTexts[0]} selected`;
      }

      return `${this.placeholder} filter: ${itemTexts.length} items selected - ${itemTexts.join(", ")}`;
    }
  }
};
</script>

<style scoped>
.filter-field {
  margin-bottom: 4px;
  padding: 0;
}

.filter-field ::v-deep .v-input__slot {
  min-height: 36px !important;
  border: none;
}

.icon-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-field ::v-deep input {
  font-size: 13px;
  padding: 0;
}

.filter-field ::v-deep .v-select__selections {
  padding: 0;
}

.filter-field ::v-deep .v-chip {
  height: 24px;
  margin: 2px;
}

.filter-field ::v-deep .v-chip__content {
  padding: 0 8px;
}

.filter-field ::v-deep input::placeholder {
  color: rgba(0, 0, 0, 0.87);
  font-size: 13px;
  font-weight: 500;
}
.filter-field ::v-deep .v-input--is-focused input::placeholder,
.filter-field ::v-deep .v-input--is-dirty input::placeholder {
  color: transparent;
}
.filter-field ::v-deep .v-input__slot:focus-within {
  outline: 2px solid #2196f3;
  outline-offset: 2px;
}
.filter-field ::v-deep .v-input__slot:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.condition-toggle:focus {
  outline: 2px solid #2196f3;
  outline-offset: 2px;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
.filter-field ::v-deep .v-label {
  color: rgba(0, 0, 0, 0.87);
}

.filter-field ::v-deep .v-icon {
  color: rgba(0, 0, 0, 0.6);
}
.filter-field ::v-deep .v-input__prepend-outer {
  margin-top: 0;
  display: flex;
  align-items: center;
  height: 36px;
}
.filter-field ::v-deep .v-input__append-inner,
.filter-field ::v-deep .v-input__prepend-inner {
  margin-top: 6px;
}

.filter-field ::v-deep .v-input__icon--clear .v-icon {
  font-size: 18px;
}
</style>
